<template>
  <article class="group h-full">
    <RouterLink
      :to="{ name: 'BlogPost', params: { slug: post.slug } }"
      class="block h-full"
      :aria-label="`Leer artículo: ${post.title}`"
    >
      <div
        class="h-full flex flex-col bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-lg hover:shadow-xl hover:border-accent-300 transition-all duration-300"
      >
        <!-- Image (only when available) -->
        <div v-if="post.featuredImage" class="aspect-video overflow-hidden relative">
          <img
            :src="post.featuredImage.url"
            :alt="post.featuredImage.alt || post.title"
            class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />

          <!-- Category Badge over image -->
          <div v-if="post.category" class="absolute top-3 left-3">
            <span
              class="inline-flex items-center px-3 py-1 bg-white/90 backdrop-blur-sm text-accent-700 text-xs font-medium rounded-full shadow-sm"
            >
              {{ post.category.name }}
            </span>
          </div>

          <!-- Featured Badge over image -->
          <div v-if="post.featured" class="absolute top-3 right-3">
            <span
              class="inline-flex items-center gap-1 px-3 py-1 bg-amber-400 text-amber-900 text-xs font-semibold rounded-full shadow-sm"
            >
              <StarIcon class="w-3 h-3" />
              Destacado
            </span>
          </div>
        </div>

        <!-- Content -->
        <div class="p-6 flex flex-col flex-1">
          <!-- Inline badges (only when no image, to keep card hierarchy) -->
          <div
            v-if="!post.featuredImage && (post.category || post.featured)"
            class="flex items-center gap-2 flex-wrap mb-3"
          >
            <span
              v-if="post.category"
              class="inline-flex items-center px-3 py-1 bg-accent-50 text-accent-700 text-xs font-medium rounded-full"
            >
              {{ post.category.name }}
            </span>
            <span
              v-if="post.featured"
              class="inline-flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-800 text-xs font-semibold rounded-full"
            >
              <StarIcon class="w-3 h-3" />
              Destacado
            </span>
          </div>

          <h3
            class="text-xl font-bold text-text-primary mb-2 group-hover:text-accent-600 transition-colors line-clamp-2"
          >
            {{ post.title }}
          </h3>

          <p class="text-text-secondary text-sm mb-4 line-clamp-2">
            {{ post.excerpt || generateExcerpt(post.content) }}
          </p>

          <!-- Tags -->
          <div v-if="post.tags && post.tags.length" class="flex flex-wrap gap-1.5 mb-4">
            <span
              v-for="tag in post.tags.slice(0, 3)"
              :key="tag"
              class="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full"
            >
              {{ tag }}
            </span>
            <span
              v-if="post.tags.length > 3"
              class="px-2 py-0.5 text-slate-500 text-xs"
            >
              +{{ post.tags.length - 3 }}
            </span>
          </div>

          <!-- Meta footer pinned to bottom -->
          <div class="mt-auto flex items-center justify-between text-sm text-slate-500 pt-4 border-t border-slate-100">
            <div class="flex items-center gap-3">
              <time class="flex items-center gap-1">
                <CalendarIcon class="w-4 h-4" />
                {{ formatDateShort(post.publishedAt) }}
              </time>
              <span>·</span>
              <span class="flex items-center gap-1">
                <ClockIcon class="w-4 h-4" />
                {{ readingTime(post.content) }} min
              </span>
            </div>

            <div class="flex items-center gap-1">
              <EyeIcon class="w-4 h-4" />
              {{ formatViews(post.views) }}
            </div>
          </div>
        </div>
      </div>
    </RouterLink>
  </article>
</template>

<script setup>
import { RouterLink } from 'vue-router'
import {
  StarIcon,
  CalendarIcon,
  ClockIcon,
  EyeIcon
} from '@heroicons/vue/24/outline'

defineProps({
  post: {
    type: Object,
    required: true
  }
})

const generateExcerpt = (content, maxLength = 150) => {
  if (!content) return 'Sin contenido disponible...'
  const textContent = content.replace(/<[^>]*>/g, '').trim()
  if (textContent.length <= maxLength) return textContent
  return textContent.slice(0, maxLength).trim() + '...'
}

const formatDateShort = (date) => {
  if (!date) return ''
  const options = { month: 'short', day: 'numeric' }
  return new Date(date).toLocaleDateString('es-ES', options)
}

const readingTime = (content) => {
  if (!content) return 1
  const wordsPerMinute = 200
  const textContent = content.replace(/<[^>]*>/g, '').trim()
  const wordCount = textContent.split(/\s+/).length
  return Math.max(1, Math.ceil(wordCount / wordsPerMinute))
}

const formatViews = (views) => {
  if (!views || views === 0) return '0'
  if (views >= 1000000) return (views / 1000000).toFixed(1) + 'M'
  if (views >= 1000) return (views / 1000).toFixed(1) + 'K'
  return views.toString()
}
</script>

<style scoped>
@reference "../../assets/styles/main.css";

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

a:focus {
  outline: 2px solid theme('colors.accent.500');
  outline-offset: 2px;
  border-radius: 1rem;
}

@media (prefers-reduced-motion: reduce) {
  .group-hover\:scale-105 {
    transform: none;
  }
}
</style>
